import { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, Trash2, Info } from "lucide-react";

export type ConfirmVariant = "danger" | "warning" | "default";

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

export function useConfirm() {
  return useContext(ConfirmContext);
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);

  const confirm: ConfirmFn = useCallback(
    (options) => new Promise<boolean>((resolve) => setState({ ...options, resolve })),
    [],
  );

  const resolve = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmModal {...state} onResolve={resolve} />}
    </ConfirmContext.Provider>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const VARIANT = {
  danger: {
    icon: <Trash2 size={20} className="text-red-500" />,
    iconBg: "bg-red-50",
    btn: "bg-red-600 hover:bg-red-700 text-white",
  },
  warning: {
    icon: <AlertTriangle size={20} className="text-amber-500" />,
    iconBg: "bg-amber-50",
    btn: "bg-amber-500 hover:bg-amber-600 text-white",
  },
  default: {
    icon: <Info size={20} className="text-blue-500" />,
    iconBg: "bg-blue-50",
    btn: "bg-gray-900 hover:bg-gray-700 text-white",
  },
};

function ConfirmModal({
  title,
  message,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  variant = "default",
  onResolve,
}: ConfirmOptions & { onResolve: (v: boolean) => void }) {
  const v = VARIANT[variant];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" }}
      onClick={() => onResolve(false)}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${v.iconBg}`}>
            {v.icon}
          </div>
          <h2 className="text-[15px] font-semibold text-gray-900 mb-1">{title}</h2>
          {message && <p className="text-sm text-gray-500 leading-relaxed">{message}</p>}
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button
            onClick={() => onResolve(false)}
            className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            autoFocus
            onClick={() => onResolve(true)}
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${v.btn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
